import {closestCorners} from '@dnd-kit/core';
import type {
    Active,
    Collision,
    CollisionDetection,
    DragEndEvent,
    DragStartEvent,
} from '@dnd-kit/core';

import {
    safelyGetGraphQLErrorMessage,
    type GraphQLErrorObject,
} from 'nutshell-core/utils/graphql-errors';

import type {FieldCreateInput} from 'nutshell-graphql-types';

import {
    convertGraphQLResponseToFormFieldState,
    getFieldTypeFromDisplayType,
    translateSchemaEntityTypeToGraphQL,
} from '../../../helpers';
import type {Form} from '../../../types';

import {
    formFieldRowToSelectedExistingField,
    isFormFieldInputRow,
    resolveCustomFieldViewForDroppedExistingField,
} from '../sidebar/add-custom-field/helpers';
import type {
    DraggableCardData,
    FieldWithArrayKey,
    FormFieldDragData,
} from '../sidebar/blocks-and-design/content-blocks/types';
import type {Field} from '../types';

import {FORM_EDITOR_INSERT_DROPPABLE_PREFIX} from './consts';
import type {FormEditorDragEndDeps, FormEditorInsertDroppableCoords} from './types';

/** Multi-page editor: `insert:<pageIndex>:<insertIndex>` */
export function formEditorInsertDroppableId(pageIndex: number, insertIndex: number): string {
    return `${FORM_EDITOR_INSERT_DROPPABLE_PREFIX}${pageIndex}:${insertIndex}`;
}

export function parseFormEditorInsertDroppable(
    overId: string
): FormEditorInsertDroppableCoords | null {
    if (!overId.startsWith(FORM_EDITOR_INSERT_DROPPABLE_PREFIX)) {
        return null;
    }
    const rest = overId.slice(FORM_EDITOR_INSERT_DROPPABLE_PREFIX.length);
    const parts = rest.split(':');
    if (parts.length === 2) {
        const pageIndex = parseInt(parts[0] ?? '', 10);
        const insertIndex = parseInt(parts[1] ?? '', 10);
        if (Number.isFinite(pageIndex) && Number.isFinite(insertIndex)) {
            return {pageIndex, insertIndex};
        }
    }
    return null;
}

function dragDataToFieldCreateInput(
    dragData: FormFieldDragData,
    formId: string
): FieldCreateInput | null {
    if (dragData.kind === 'header') {
        return {type: dragData.fieldType, formId};
    }
    if (dragData.kind === 'input') {
        return {
            // Support special input types, like address or file upload
            type: getFieldTypeFromDisplayType(dragData.input.displayType),
            fieldName: dragData.input.fieldKey,
            formId,
            entityName: translateSchemaEntityTypeToGraphQL(dragData.input.entityType),
        };
    }

    if (dragData.kind === 'existing-custom') {
        return {
            type: getFieldTypeFromDisplayType(dragData.input.displayType),
            formId,
            entityName: translateSchemaEntityTypeToGraphQL(dragData.input.entityType),
            entityAttributeId: dragData.input.fieldKey,
        };
    }

    // 'custom' (short-text, long-text, etc.) requires a configuration sidebar
    // state, not yet supported
    return null;
}

/** Sidebar palette cards attach `fieldData`; canvas sortable rows do not. */
export function isPaletteDragActive(active: Active): boolean {
    return Boolean(active.data.current?.fieldData);
}

export function handleFormEditorDragStart(
    event: DragStartEvent,
    setSidebarDragCard: (value: DraggableCardData | null) => void,
    setReorderingFieldKey: (value: string | null) => void
): void {
    const data = event.active.data.current as DraggableCardData | undefined;
    if (data?.fieldData) {
        setSidebarDragCard(data);
        setReorderingFieldKey(null);
    } else {
        setSidebarDragCard(null);
        setReorderingFieldKey(String(event.active.id));
    }
}

function tryDropFieldOnInsertZone(event: DragEndEvent, deps: FormEditorDragEndDeps): boolean {
    const {active, over} = event;
    if (!over || !isPaletteDragActive(active)) return false;

    const overId = String(over.id);
    const coords = parseFormEditorInsertDroppable(overId);
    if (!coords || !deps.formId) {
        return false;
    }

    const ops = deps.dndPageFieldMutatorsRef.current.get(coords.pageIndex);
    if (!ops) {
        return false;
    }

    const {insertIndex, pageIndex} = coords;
    if (insertIndex < 0 || insertIndex > ops.fields.length) {
        return false;
    }

    const cardData = active.data.current as DraggableCardData;
    const input = dragDataToFieldCreateInput(cardData.fieldData, deps.formId);
    if (!input) {
        if (cardData.fieldData.kind === 'custom') {
            deps.setFieldCreateAtIndex({pageIndex, insertIndex});
            deps.openAddCustomFieldSidebar(cardData.fieldData.view);
            return true;
        }
        return false;
    }

    deps.setFieldCreateAtIndex({pageIndex, insertIndex});
    deps.setSidebarStateForFieldCreate('loading');
    const fieldDataKind = cardData.fieldData.kind;
    void deps
        .createField({variables: {input}})
        .then((data) => {
            const createdField = data?.data?.fieldCreate?.field;
            if (!createdField) {
                deps.setSidebarStateForFieldCreate('blocks-and-design');
                return;
            }

            const converted = convertGraphQLResponseToFormFieldState(createdField);
            ops.insert(insertIndex, converted as Form['fields'][number]);
            deps.refetchForm();

            if (fieldDataKind === 'existing-custom') {
                const dragField = cardData.fieldData as Extract<
                    FormFieldDragData,
                    {kind: 'existing-custom'}
                >;
                const view = resolveCustomFieldViewForDroppedExistingField(
                    dragField.input.displayType,
                    converted as Field
                );
                if (view && isFormFieldInputRow(converted as Field)) {
                    deps.openEditCustomFieldFromDrop({
                        view,
                        existingField: formFieldRowToSelectedExistingField(converted as Field),
                        fieldRef: {pageIndex, fieldIndex: insertIndex},
                    });
                } else {
                    deps.setSidebarStateForFieldCreate('blocks-and-design');
                }
            } else {
                deps.setSidebarStateForFieldCreate('blocks-and-design');
            }
        })
        .catch((error) => {
            deps.onFieldCreateFailed(safelyGetGraphQLErrorMessage(error as GraphQLErrorObject));
            deps.setSidebarStateForFieldCreate('blocks-and-design');
        })
        .finally(() => {
            deps.setFieldCreateAtIndex(null);
        });
    return true;
}

function tryReorderFieldRow(event: DragEndEvent, deps: FormEditorDragEndDeps): void {
    const {active, over} = event;
    if (!over || isPaletteDragActive(active)) return;

    const activeKey = String(active.id);
    const overId = String(over.id);
    if (overId.startsWith(FORM_EDITOR_INSERT_DROPPABLE_PREFIX)) return;

    const pageOpsList = Array.from(deps.dndPageFieldMutatorsRef.current.values());
    for (const ops of pageOpsList) {
        const activeIndex = ops.fields.findIndex((f: FieldWithArrayKey) => f.key === activeKey);
        if (activeIndex === -1) continue;

        const targetIndex = ops.fields.findIndex((f: FieldWithArrayKey) => f.key === overId);
        if (targetIndex === -1 || activeIndex === targetIndex) return;

        ops.move(activeIndex, targetIndex);
        return;
    }
}

export function handleFormEditorDragEnd(event: DragEndEvent, deps: FormEditorDragEndDeps): void {
    deps.clearDragUi();
    const {over} = event;
    if (!over) return;

    if (tryDropFieldOnInsertZone(event, deps)) return;

    tryReorderFieldRow(event, deps);
}

/**
 * Palette drags must use the pointer (not the drag overlay rect) so quick drops from the
 * sidebar do not snap to an insert slot based only on horizontal alignment. Requires the
 * pointer to sit inside the field column; Y position picks the slot by vertical bands
 * between adjacent insert-zone centers so thin gaps stay easy to target.
 */
function palettePointerInsertCollisions(args: Parameters<CollisionDetection>[0]): Collision[] {
    const {pointerCoordinates, droppableContainers, droppableRects, active} = args;
    if (!pointerCoordinates || !active || !isPaletteDragActive(active)) {
        return [];
    }

    const items = droppableContainers
        .filter((c) => String(c.id).startsWith(FORM_EDITOR_INSERT_DROPPABLE_PREFIX))
        .map((container) => {
            const rect = droppableRects.get(container.id);
            if (!rect) return null;
            const coords = parseFormEditorInsertDroppable(String(container.id));
            if (coords == null) return null;
            return {container, rect, coords};
        })
        .filter((x): x is NonNullable<typeof x> => x != null)
        .sort((a, b) => a.rect.top - b.rect.top);

    if (items.length === 0) {
        return [];
    }

    const minLeft = Math.min(...items.map((i) => i.rect.left));
    const maxRight = Math.max(...items.map((i) => i.rect.right));

    if (pointerCoordinates.x < minLeft || pointerCoordinates.x > maxRight) {
        return [];
    }

    const centersY = items.map((i) => i.rect.top + i.rect.height / 2);
    const n = items.length;

    for (let j = 0; j < n; j++) {
        const item = items[j];
        if (!item) continue;

        const prevCenter = j > 0 ? centersY[j - 1] : undefined;
        const thisCenter = centersY[j];
        const nextCenter = j < n - 1 ? centersY[j + 1] : undefined;
        const topBound =
            prevCenter != null && thisCenter != null ? (prevCenter + thisCenter) / 2 : -Infinity;
        const bottomBound =
            thisCenter != null && nextCenter != null ? (thisCenter + nextCenter) / 2 : Infinity;

        if (pointerCoordinates.y >= topBound && pointerCoordinates.y < bottomBound) {
            return [
                {
                    id: item.container.id,
                    data: {
                        droppableContainer: item.container,
                        value: 0,
                    },
                },
            ];
        }
    }

    return [];
}

export const formEditorListCollisionDetection: CollisionDetection = (args) => {
    if (!args.active) {
        return closestCorners(args);
    }

    if (isPaletteDragActive(args.active)) {
        return palettePointerInsertCollisions(args);
    }

    return closestCorners(args);
};
